import { TCardFacet, TFacet } from '@/types/collection'

/**
 * Result type for facet value matching
 */
export interface FacetMatchResult {
  facet: TFacet | TCardFacet
  value: {
    seo_alias: string
    title: string
    [key: string]: unknown
  }
}

/**
 * Tìm facet value khớp nhất với concat string
 * Sử dụng longest-match strategy để tránh match sai
 *
 * @example
 * ```ts
 * const result = findMatchingFacetValue('item-dai-tay', facets, cardFacets)
 * if (result) {
 *   console.log(result.facet.seo_alias) // 'phu-hop-voi'
 *   console.log(result.value.seo_alias) // 'item-dai-tay'
 * }
 * ```
 *
 * @param concatStr - Concat string từ URL (ví dụ: 'item-dai-tay')
 * @param facets - Danh sách facets từ API
 * @param cardFacets - Danh sách card facets từ API
 * @returns Facet và value match nhất, hoặc null nếu không tìm thấy
 */
export function findMatchingFacetValue(
  concatStr: string,
  facets: TFacet[],
  cardFacets: TCardFacet[]
): FacetMatchResult | null {
  const allFacets = [...facets, ...cardFacets]

  for (const facet of allFacets) {
    if (!facet?.is_concat_slug) continue

    // First try exact match
    const exactMatch = facet.facet_values?.find(
      (v) => v.seo_alias === concatStr
    )
    if (exactMatch) {
      return {
        facet,
        value: exactMatch as unknown as {
          seo_alias: string
          title: string
          [key: string]: unknown
        },
      }
    }

    // If no exact match, try partial matching with longest match strategy
    // Điều này đảm bảo 'item-dai-tay' được chọn thay vì 'item-dai'
    const matchingValue = facet.facet_values
      ?.filter((v) => concatStr.includes(v.seo_alias))
      .sort((a, b) => b.seo_alias.length - a.seo_alias.length)[0]

    if (matchingValue) {
      return {
        facet,
        value: matchingValue as unknown as {
          seo_alias: string
          title: string
          [key: string]: unknown
        },
      }
    }
  }

  return null
}

/**
 * Check xem facet key có phải là concat filter không
 *
 * @example
 * ```ts
 * if (isConcatFacet('phu-hop-voi', facets, cardFacets)) {
 *   // Handle concat filter logic
 * }
 * ```
 *
 * @param key - Facet key cần check (ví dụ: 'phu-hop-voi')
 * @param facets - Danh sách facets từ API
 * @param cardFacets - Danh sách card facets từ API
 * @returns true nếu là concat filter, false nếu không
 */
export function isConcatFacet(
  key: string,
  facets: TFacet[],
  cardFacets: TCardFacet[]
): boolean {
  return [...facets, ...cardFacets].some(
    (f) => f.seo_alias === key && f.is_concat_slug
  )
}

/**
 * Lấy concat facet đầu tiên
 * Thường chỉ có 1 concat facet tại 1 thời điểm
 *
 * @example
 * ```ts
 * const concatFacet = getConcatFacet(facets, cardFacets)
 * if (concatFacet) {
 *   console.log(concatFacet.seo_alias) // 'phu-hop-voi'
 * }
 * ```
 *
 * @param facets - Danh sách facets từ API
 * @param cardFacets - Danh sách card facets từ API
 * @returns Concat facet đầu tiên, hoặc null nếu không có
 */
export function getConcatFacet(
  facets: TFacet[],
  cardFacets: TCardFacet[]
): TFacet | TCardFacet | null {
  return [...facets, ...cardFacets].find((f) => f.is_concat_slug) ?? null
}

/**
 * Compute concat value từ filters và facets
 * Helper function để tính toán concat value từ filter values
 *
 * @example
 * ```ts
 * const concatValue = computeConcatValue(
 *   { 'phu-hop-voi': 'item-dai' },
 *   facets,
 *   cardFacets
 * )
 * console.log(concatValue) // 'item-dai'
 * ```
 *
 * @param filters - Filter values từ store
 * @param facets - Danh sách facets từ API
 * @param cardFacets - Danh sách card facets từ API
 * @returns Concat value (string), hoặc null nếu không có concat filter
 */
export function computeConcatValue(
  filters: Record<string, string | string[] | number | undefined>,
  facets: TFacet[],
  cardFacets: TCardFacet[]
): string | null {
  const concatFacet = getConcatFacet(facets, cardFacets)
  if (!concatFacet) return null

  const value = filters[concatFacet.seo_alias]
  if (!value) return null

  return Array.isArray(value) ? value[0] : String(value)
}

/**
 * Priority order for concat facets (color has highest priority)
 */
const CONCAT_FACET_PRIORITY = ['color', 'mau-sac']

/**
 * Tìm facet value khớp với concat string, với priority cho color
 * Color sẽ được ưu tiên trước các facets khác
 *
 * @example
 * ```ts
 * const result = findMatchingFacetValueWithPriority('trang', facets, cardFacets)
 * if (result) {
 *   console.log(result.facet.seo_alias) // 'color' (not 'phu-hop-voi')
 *   console.log(result.value.seo_alias) // 'trang'
 * }
 * ```
 *
 * @param concatStr - Concat string từ URL (ví dụ: 'trang')
 * @param facets - Danh sách facets từ API
 * @param cardFacets - Danh sách card facets từ API
 * @returns Facet và value match với priority, hoặc null nếu không tìm thấy
 */
export function findMatchingFacetValueWithPriority(
  concatStr: string,
  facets: TFacet[],
  cardFacets: TCardFacet[]
): FacetMatchResult | null {
  const allFacets = [...facets, ...cardFacets]

  // Filter concat facets and deduplicate by seo_alias (keep first occurrence)
  const concatFacets = allFacets.filter((f) => f.is_concat_slug)
  const seenAliases = new Set<string>()
  const uniqueFacets = concatFacets.filter((f) => {
    if (seenAliases.has(f.seo_alias)) {
      return false
    }
    seenAliases.add(f.seo_alias)
    return true
  })

  // Sort by priority (color first)
  const sortedFacets = uniqueFacets.sort((a, b) => {
    const priorityA = CONCAT_FACET_PRIORITY.indexOf(a.seo_alias)
    const priorityB = CONCAT_FACET_PRIORITY.indexOf(b.seo_alias)
    if (priorityA >= 0 && priorityB >= 0) return priorityA - priorityB
    if (priorityA >= 0) return -1
    if (priorityB >= 0) return 1
    return 0
  })

  // Try to match with sorted facets (color first)
  for (const facet of sortedFacets) {
    const exactMatch = facet.facet_values?.find(
      (v) => v.seo_alias === concatStr
    )
    if (exactMatch) {
      return {
        facet,
        value: exactMatch as unknown as {
          seo_alias: string
          title: string
          [key: string]: unknown
        },
      }
    }
  }

  console.warn(
    '⚠️ [findMatchingFacetValueWithPriority] No match found for:',
    concatStr
  )
  return null
}
